// ----------------------------------------------------------------------

function pxToRem(value) {
  return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm)
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md)
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg)
    }
  };
}

const FONT_PRIMARY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
// Amik display face — Poppins for headings; Inter remains the body/UI face.
const FONT_DISPLAY = 'Poppins, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// When isAmik is true, headings switch to Poppins with the brand's tighter
// tracking. All other tenants keep the original Inter-only scale unchanged.
export function createTypography(isAmik = false) {
  const displayFamily = isAmik ? FONT_DISPLAY : FONT_PRIMARY;
  const headTracking = isAmik ? '-0.02em' : undefined;

  return {
    fontFamily: FONT_PRIMARY,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontFamily: displayFamily,
      fontWeight: 700,
      lineHeight: 1.2,
      fontSize: pxToRem(40),
      letterSpacing: isAmik ? '-0.03em' : '-0.02em',
      ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 })
    },
    h2: {
      fontFamily: displayFamily,
      fontWeight: 700,
      lineHeight: 1.3,
      fontSize: pxToRem(32),
      letterSpacing: isAmik ? '-0.02em' : '-0.01em',
      ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 })
    },
    h3: {
      fontFamily: displayFamily,
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: pxToRem(24),
      letterSpacing: headTracking,
      ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 })
    },
    h4: {
      fontFamily: displayFamily,
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: pxToRem(20),
      letterSpacing: headTracking,
      ...responsiveFontSizes({ sm: 20, md: 24, lg: 24 })
    },
    h5: {
      fontFamily: displayFamily,
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: pxToRem(18),
      letterSpacing: headTracking,
      ...responsiveFontSizes({ sm: 19, md: 20, lg: 20 })
    },
    h6: {
      fontFamily: displayFamily,
      fontWeight: 700,
      lineHeight: 28 / 18,
      fontSize: pxToRem(17),
      letterSpacing: headTracking,
      ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 })
    },
    subtitle1: {
      fontWeight: 600,
      lineHeight: 1.5,
      fontSize: pxToRem(16)
    },
    subtitle2: {
      fontWeight: 600,
      lineHeight: 22 / 14,
      fontSize: pxToRem(14)
    },
    body1: {
      lineHeight: 1.5,
      fontSize: pxToRem(16)
    },
    body2: {
      lineHeight: 22 / 14,
      fontSize: pxToRem(14)
    },
    caption: {
      lineHeight: 1.5,
      fontSize: pxToRem(12)
    },
    overline: {
      // Amik eyebrows: uppercase Poppins 600 with wide tracking.
      fontFamily: isAmik ? FONT_DISPLAY : FONT_PRIMARY,
      fontWeight: isAmik ? 600 : 700,
      lineHeight: 1.5,
      fontSize: pxToRem(12),
      letterSpacing: isAmik ? '0.14em' : 1.1,
      textTransform: 'uppercase'
    },
    button: {
      fontWeight: isAmik ? 600 : 700,
      lineHeight: 24 / 14,
      fontSize: pxToRem(14),
      textTransform: isAmik ? 'none' : 'capitalize'
    }
  };
}

// Default export kept for any non-themed importers (neutral / non-Amik).
const typography = createTypography(false);

export default typography;
